(() => {
  'use strict';
  if (window.cbWishlist) return;
  const script = document.currentScript;
  const cardsEnabled = script?.dataset.cardToggles === 'true';
  const selector = 'button.wishlist-icon[data-product-id]';
  const counts = '.site-header__icon--wishlist [data-count], .res-nav__wishlist-count';
  const journalKey = `cb:wishlist:pending:v1:${script?.dataset.shop || location.hostname}:${script?.dataset.customer || 'guest'}`;
  const refreshKey = journalKey + ':refresh';
  const pending = new Map();
  let api, initialized = false, initializing, hasSnapshot = false, headerWritten = false;
  let products = [], running = false, blocked = false, inFlight, renderQueued = false;
  let startupTimer, notice, noticeText, noticeTimer, noticeId, navigation;
  let revision = 0, restored = false, storageAvailable = true, startupFailed = false;
  let leaving = false, interrupted = false, protecting = false, refreshOnStart = false;
  let pageHidden = false, resumeTimer, reconciliation;
  let reconcileRequired = false;
  const beforeUnload = () => {
    leaving = true;
    interrupted = Boolean(pending.size || inFlight);
    cancelNavigation();
    clearTimeout(resumeTimer);
    if (!pageHidden) resumeTimer = setTimeout(resume, 10000);
  };
  const protectNavigation = () => {
    const needed = Boolean(pending.size || running);
    if (needed === protecting) return;
    protecting = needed;
    window[needed ? 'addEventListener' : 'removeEventListener']('beforeunload', beforeUnload);
  };

  const persist = () => {
    protectNavigation();
    if (pageHidden) return;
    try {
      if (pending.size) sessionStorage.setItem(journalKey, JSON.stringify({
        time: Date.now(), entries: [...pending].map(([id, intent]) => ({ id, ...intent }))
      }));
      else sessionStorage.removeItem(journalKey);
    } catch (error) {
      storageAvailable = false;
      try { sessionStorage.removeItem(journalKey); } catch (storageError) {}
    }
  };
  try {
    refreshOnStart = sessionStorage.getItem(refreshKey) === '1';
    sessionStorage.removeItem(refreshKey);
    const saved = JSON.parse(sessionStorage.getItem(journalKey) || 'null');
    if (saved && Date.now() - saved.time < 300000 && Date.now() >= saved.time && Array.isArray(saved.entries)) {
      saved.entries.forEach(entry => {
        if (!Number.isSafeInteger(entry.id) || entry.id <= 0 || typeof entry.target !== 'boolean') return;
        pending.set(entry.id, {
          target: entry.target,
          revision: ++revision,
          removeItemId: typeof entry.removeItemId === 'string' || typeof entry.removeItemId === 'number' ? entry.removeItemId : null
        });
      });
      restored = pending.size > 0;
    } else sessionStorage.removeItem(journalKey);
  } catch (error) {
    storageAvailable = false;
  }
  const ids = () => new Set(products.map(product => Number(product.id)));
  const active = id => pending.has(id) ? pending.get(id).target : ids().has(id);
  const setAttribute = (node, name, value) => {
    if (node.getAttribute(name) !== value) node.setAttribute(name, value);
  };
  const render = () => {
    renderQueued = false;
    const saved = ids();
    let count = products.length;
    pending.forEach((intent, id) => {
      if (intent.target && !saved.has(id)) count++;
      if (!intent.target && saved.has(id) && (intent.removeItemId == null || products.some(product => product.wishlist_item_id === intent.removeItemId))) count--;
    });
    if (hasSnapshot || headerWritten) {
      headerWritten = true;
      document.querySelectorAll(counts).forEach(node => {
        const value = count > 0 ? String(count) : '';
        if (node.textContent !== value) node.textContent = value;
      });
    }
    if (!cardsEnabled) return;
    document.querySelectorAll(selector).forEach(node => {
      const id = Number(node.dataset.productId);
      const added = pending.has(id) ? pending.get(id).target : saved.has(id);
      if (node.classList.contains('added') !== added) node.classList.toggle('added', added);
      setAttribute(node, 'aria-pressed', String(added));
      setAttribute(node, 'aria-busy', String(pending.has(id) || inFlight?.id === id));
      if (!node.hasAttribute('aria-label')) node.setAttribute('aria-label', script.dataset.label);
    });
  };
  const scheduleRender = () => {
    if (!renderQueued) {
      renderQueued = true;
      queueMicrotask(render);
    }
  };
  const readState = () => {
    const model = api?.collection?.items?.[0];
    if (model) {
      products = [...(model.products || [])];
      hasSnapshot = true;
    }
  };
  const sync = () => {
    if (running || leaving) return;
    readState();
    render();
  };
  const hideError = id => {
    if (id !== undefined && noticeId !== id) return;
    clearTimeout(noticeTimer);
    if (notice) {
      notice.hidden = true;
      notice.style.display = 'none';
    }
  };
  const showError = (slow = false, id = null) => {
    if (!notice) {
      notice = document.createElement('div');
      notice.dataset.wishlistSaveError = '';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'position:fixed;bottom:calc(88px + env(safe-area-inset-bottom));left:16px;right:16px;max-width:440px;margin:auto;padding:16px 20px;background:#fff;color:#111;border:1px solid currentColor;box-shadow:0 4px 20px #0002;z-index:10000;font:inherit;line-height:1.4;display:flex;align-items:center;gap:16px;';
      noticeText = document.createElement('span');
      const close = document.createElement('button');
      close.type = 'button';
      close.textContent = '×';
      close.setAttribute('aria-label', script.dataset.dismissError || script.dataset.label);
      close.style.cssText = 'border:0;background:none;color:inherit;font:inherit;font-size:24px;padding:4px;cursor:pointer;';
      notice.addEventListener('click', () => hideError());
      notice.append(noticeText, close);
      document.body.appendChild(notice);
    }
    noticeId = id;
    const title = !slow && id != null ? document.querySelector(`button.wishlist-icon[data-product-id="${id}"]`)?.closest('.product-item')?.querySelector('.product-item__title')?.textContent?.trim() : '';
    const message = slow ? (script.dataset.saveTimeout || script.dataset.saveError) : script.dataset.saveError;
    noticeText.textContent = title ? `${title}: ${message}` : message;
    notice.hidden = false;
    notice.style.display = 'flex';
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => hideError(), 6000);
  };
  const cancelNavigation = () => {
    if (navigation) clearTimeout(navigation.timer);
    navigation = null;
  };
  const navigate = () => {
    if (!navigation) return;
    const { href } = navigation;
    cancelNavigation();
    beforeUnload();
    location.assign(href);
  };
  const failWaiting = () => {
    if (leaving) return;
    if (!api) startupFailed = true;
    clearTimeout(startupTimer);
    startupTimer = null;
    if (!pending.size || running) return;
    cancelNavigation();
    pending.clear();
    persist();
    readState();
    render();
    showError(!api);
  };
  const armStartup = () => {
    if (!startupTimer) startupTimer = setTimeout(failWaiting, 60000);
  };
  const withTimeout = promise => new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Wishlist loading timed out')), 15000);
    Promise.resolve(promise).then(value => {
      clearTimeout(timer);
      resolve(value);
    }, error => {
      clearTimeout(timer);
      reject(error);
    });
  });
  const save = async (id, intent) => {
    if (intent.target) {
      if (!ids().has(id)) await api.addProduct(id);
      return;
    }
    const item = intent.removeItemId == null
      ? products.find(product => Number(product.id) === id)
      : products.find(product => product.wishlist_item_id === intent.removeItemId);
    if (!item) return;
    if (item.wishlist_item_id != null && typeof api.removeItem === 'function') {
      intent.removeItemId = item.wishlist_item_id;
      persist();
      await api.removeItem(intent.removeItemId);
    } else await api.removeProduct(id);
  };
  const notifyProduct = (id, added) => {
    try {
      window.app?.emit?.(added ? 'wishlist:added' : 'wishlist:removed', { productId: id });
    } catch (error) {}
  };
  const flush = async () => {
    if (running || !initialized || blocked || leaving || reconcileRequired) return;
    running = true;
    try {
      while (pending.size && !leaving && !reconcileRequired) {
        const [id, intent] = pending.entries().next().value;
        inFlight = { id, intent };
        let timer;
        const completion = save(id, intent).then(
          () => ({ ok: true }), error => ({ ok: false, error })
        );
        const result = await Promise.race([
          completion,
          new Promise(resolve => { timer = setTimeout(() => resolve({ timeout: true }), 20000); })
        ]);
        clearTimeout(timer);
        if (leaving) {
          if (result.timeout) await completion;
          return;
        }
        if (result.timeout) {
          blocked = true;
          cancelNavigation();
          pending.clear();
          persist();
          readState();
          render();
          showError(true, id);
          const outcome = await completion;
          if (leaving) return;
          readState();
          if (outcome.ok) {
            hideError(id);
            notifyProduct(id, intent.target);
          } else showError(false, id);
          break;
        }
        readState();
        if (result.ok) {
          if (pending.get(id) === intent) pending.delete(id);
          hideError(id);
          notifyProduct(id, intent.target);
        } else if (!reconcileRequired) {
          pending.delete(id);
          cancelNavigation();
          showError(false, id);
        }
        inFlight = null;
        persist();
        render();
      }
    } finally {
      inFlight = null;
      blocked = false;
      running = false;
      protectNavigation();
      if (!leaving) {
        readState();
        render();
        if (reconcileRequired) void reconcile();
        else if (!pending.size) navigate();
      }
    }
  };
  const reconcile = () => {
    if (!reconcileRequired || reconciliation || running || !api || !initialized || leaving || pageHidden) return;
    initialized = false;
    reconciliation = (async () => {
      const collection = api.collection;
      collection.clearCache();
      collection.apiPromise = null;
      collection.cachePromise = null;
      collection._loaded = false;
      await withTimeout(collection.load(true));
      readState();
      initialized = true;
      if (leaving || pageHidden) return;
      reconcileRequired = false;
      clearTimeout(startupTimer);
      startupTimer = null;
      render();
      void flush();
    })().catch(() => {
      initialized = false;
      initializing = null;
      if (!leaving && !pageHidden) {
        pending.clear();
        persist();
        readState();
        render();
        showError(true);
      }
    }).finally(() => { reconciliation = null; });
  };
  const initialize = () => {
    if (!api || initializing || initialized) return;
    initializing = (async () => {
      await withTimeout(api.collection.get());
      readState();
      render();
      if (api.collection.apiPromise) await withTimeout(api.collection.apiPromise);
      else if (refreshOnStart || (restored && pending.size)) await withTimeout(api.collection.load(true));
      restored = false;
      refreshOnStart = false;
      readState();
      initialized = true;
      clearTimeout(startupTimer);
      startupTimer = null;
      render();
      if (reconcileRequired) void reconcile();
      else void flush();
    })().catch(() => {
      initializing = null;
      failWaiting();
    });
  };
  const connect = wk => {
    startupFailed = false;
    clearTimeout(startupTimer);
    startupTimer = null;
    if (!api) {
      api = wk;
      api.collection.on('change', sync);
      api.collection.on('load', sync);
    }
    initialize();
  };
  const toggle = id => {
    if (startupFailed && !api) {
      showError(true);
      return;
    }
    if (blocked) {
      showError(true, inFlight?.id);
      return;
    }
    hideError();
    const target = !active(id);
    pending.set(id, {
      target,
      revision: ++revision,
      removeItemId: target ? null : products.find(product => Number(product.id) === id)?.wishlist_item_id ?? null
    });
    persist();
    requestAnimationFrame(() => {
      render();
      setTimeout(flush, 0);
    });
    if (!initialized) {
      armStartup();
      initialize();
    }
  };
  document.addEventListener('click', event => {
    const button = event.target.closest?.(selector);
    if (cardsEnabled && button && !button.disabled) {
      const id = Number(button.dataset.productId);
      if (!Number.isSafeInteger(id) || id <= 0) return;
      event.preventDefault();
      toggle(id);
      return;
    }
    const link = event.target.closest?.('a[href]');
    const wishlistLink = document.querySelector('a.site-header__icon--wishlist');
    if (!link || !wishlistLink || link.href !== wishlistLink.href || (!pending.size && !inFlight) || blocked) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    event.preventDefault();
    cancelNavigation();
    navigation = { href: link.href, timer: setTimeout(() => {
      if (storageAvailable) navigate();
      else showError(true);
    }, 3000) };
  }, true);
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 &&
      (node.matches?.(selector + ', .site-header__icon--wishlist, .res-nav__wishlist-count') || node.querySelector?.(selector + ', .site-header__icon--wishlist, .res-nav__wishlist-count'))))) scheduleRender();
  }).observe(document.documentElement, { childList: true, subtree: true });
  const resume = () => {
    if (!leaving || pageHidden) return;
    clearTimeout(resumeTimer);
    leaving = false;
    interrupted = false;
    reconcileRequired = true;
    readState();
    persist();
    render();
    if (initialized) void reconcile();
    else initialize();
  };
  const resumeOnUse = event => { if (!event.target?.closest?.('a[href]')) resume(); };
  document.addEventListener('pointerdown', resumeOnUse, true);
  document.addEventListener('keydown', resumeOnUse, true);
  window.addEventListener('pagehide', () => { pageHidden = true; clearTimeout(resumeTimer); beforeUnload(); });
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      let journalPresent = false;
      try { journalPresent = Boolean(sessionStorage.getItem(journalKey)); } catch (error) {}
      if (interrupted || pending.size || journalPresent) {
        leaving = true;
        try { sessionStorage.setItem(refreshKey, '1'); } catch (error) {}
        location.reload();
        return;
      }
      leaving = false;
      pageHidden = false;
    }
    if (initialized && !leaving) sync();
  });
  window.cbWishlist = { cardsEnabled, connect, mount: scheduleRender, failWaiting, loading: () => {
    startupFailed = false;
    clearTimeout(startupTimer);
    startupTimer = null;
    armStartup();
  } };
  if (restored) armStartup();
  protectNavigation();
  render();
})();
